import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Facility, FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';
import { AuthService } from '../../services/auth.service';
import { ClientMatterService } from '../../services/client-matter.service';
import { MoveService } from '../../services/move.service';
import { ClientProfileComponent } from '../clientprofile/clientprofile.component';
import { SharingPatientId } from '../sharing-setup/sharing-setup.component';
import { PatientOverviewData } from './patient-overview.models';

@Injectable()
export class PatientOverviewFacade {
  facilities: Facility[] = [];
  showFacilityRequests = false;
  currentPage = 1;
  totalPages = 4;
  selectedPatientId: SharingPatientId | null = null;
  currentFacilityPatientId: SharingPatientId | null = null;
  otherPatientIDs: SharingPatientId[] = [];
  shareDirection: 'withFacility' | 'fromFacility' | null = null;
  patient: any;
  isImageShareMode = false;
  isIframeLoading = false;
  currentFacility = '';
  selectedImageShareUrl = '';
  activeImageShareTab: 'pacs' | 'nuage' | null = null;
  shareTypeSelected: 'document' | 'image' | null = null;
  patientIdsResetToken = 0;

  private dialogRef?: MatDialogRef<any>;

  constructor(
    private dialog: MatDialog,
    private auth: AuthService,
    private clientMatter: ClientMatterService,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private moveService: MoveService
  ) {}

  async initialize(dialogRef: MatDialogRef<any> | null, data: PatientOverviewData): Promise<void> {
    const currentFacility = this.auth.userData.getValue()['facility'] || '';

    // Primary source: Orthanc modalities — these are the facilities this instance can actually talk to
    const [modalitiesResp, allFirestoreFacilities] = await Promise.all([
      firstValueFrom(this.moveService.getModalities()).catch(() => ({ modalities: [] })),
      this.firebaseUtilities_$.getFacilities(),
    ]);

    const modalities = modalitiesResp?.modalities || [];

    // Normalize a facility/modality id for comparison: lowercase, strip separators.
    // Must be defined before firestoreMap so we can use it as the map key.
    const norm = (s: string) => s.toLowerCase().replace(/[-_\s]/g, '');

    // Key by normalized ID so 'SITEB' matches 'site-b' (and any other separator variant).
    const firestoreMap = new Map(allFirestoreFacilities.map(f => [norm(f.id), f]));

    // Build facility list from Orthanc modalities, enriched with Firestore display details.
    // Modalities that match a Firestore facility are NuagedX siblings; the rest are external DICOM devices.
    this.facilities = modalities
      .filter(m => norm(m.name) !== norm(currentFacility))
      .map(m => {
        const match =
          firestoreMap.get(norm(m.name)) ||
          firestoreMap.get(norm(m.aet || ''));
        return {
          ...(match || {}),
          id: match?.id || m.name,
          name: match?.name || m.name,
          aet: m.aet,
        } as Facility;
      });

    this.dialogRef = dialogRef || undefined;
    this.resetState();

    this.patient = data.patient;
    this.currentFacility = currentFacility;

    if (this.patient?.metadata?.medical_record_numbers && !this.patient.patientIDs) {
      this.patient.patientIDs = this.patient.metadata.medical_record_numbers;
    }

    this.refreshPatientIdBuckets();

    if (this.dialogRef) {
      this.dialogRef.beforeClosed().subscribe(() => this.dialogRef?.removePanelClass('fullscreen-dialog'));
    }
  }

  onSelectedPatientIdEvent(patientId: SharingPatientId): void {
    this.shareTypeSelected = 'image';
    this.selectedPatientId = patientId;
    this.refreshPatientIdBuckets();
  }

  onSharingFacilitySelected(patientId: SharingPatientId): void {
    this.onSelectedPatientIdEvent(patientId);
  }

  onShareWithFacility(): void {
    this.shareTypeSelected = 'image';
    this.shareDirection = 'withFacility';
    this.selectedPatientId = null;
    this.patientIdsResetToken++;
    this.clampCurrentPageToAllowed();
  }

  onShareFromFacility(): void {
    this.shareTypeSelected = 'image';
    this.shareDirection = 'fromFacility';
    this.selectedPatientId = null;
    this.patientIdsResetToken++;
    this.clampCurrentPageToAllowed();
  }

  onSharingGoNext(): void {
    this.goToFullscreenMode();
  }

  onClose(): void {
    this.dialogRef?.removePanelClass('fullscreen-dialog');
    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    this.dialogRef?.close();
  }

  async onDocumentShare(): Promise<void> {
    this.shareTypeSelected = 'document';
    this.patient =
      !this.patient || Object.keys(this.patient).length === 1 ? this.auth.getPatientProfile() : this.patient;

    if (!this.patient.caseName && this.patient.LegalCaseId) {
      try {
        const completePatientData = await this.clientMatter.getPatientData(this.patient.LegalCaseId);
        this.patient = { ...this.patient, ...completePatientData };
      } catch (error) {
        console.warn('Could not load complete patient data:', error);
      }
    }

    if (this.patient?.metadata?.medical_record_numbers && !this.patient.patientIDs) {
      this.patient.patientIDs = this.patient.metadata.medical_record_numbers;
    }

    this.refreshPatientIdBuckets();

    const dialogConfig: MatDialogConfig = {
      width: '95%',
      minWidth: '95%',
      maxWidth: '95%',
      height: '90%',
      id: 'client-profile',
      data: { patient: this.patient }
    };

    this.dialog.open(ClientProfileComponent, dialogConfig);
    this.dialogRef?.close();
  }

  goToFullscreenMode(targetTab?: 'pacs' | 'nuage'): void {
    this.shareTypeSelected = 'image';
    const resolvedTab: 'pacs' | 'nuage' = targetTab ?? (this.shareDirection === 'fromFacility' ? 'nuage' : 'pacs');

    this.isImageShareMode = true;
    this.isIframeLoading = true;
    this.dialogRef?.addPanelClass('fullscreen-dialog');
    this.dialogRef?.updatePosition({ top: '0px', left: '0px', right: '0px', bottom: '0px' });
    this.dialogRef?.updateSize('100vw', '100vh');

    this.activeImageShareTab = resolvedTab;
    this.selectedImageShareUrl = '';
  }

  onImageShare(): void {
    this.shareTypeSelected = 'image';
    this.shareDirection = null;
    this.selectedPatientId = null;
    this.patientIdsResetToken++;
    this.clampCurrentPageToAllowed();
  }

  onPageChange(page: number): void {
    const maxAllowed = this.getMaxAllowedStep();
    const targetPage = page <= maxAllowed ? page : maxAllowed;
    this.currentPage = targetPage;
  }

  onRestartSharingSetup(): void {
    this.dialogRef?.removePanelClass('fullscreen-dialog');
    this.dialogRef?.updateSize();
    this.dialogRef?.updatePosition();

    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    this.shareDirection = null;
    this.selectedPatientId = null;
    this.currentPage = 1;
    this.patientIdsResetToken++;
    this.shareTypeSelected = null;
    this.clampCurrentPageToAllowed();
  }

  onIframeLoaded(): void {
    this.isIframeLoading = false;
  }

  get activeImageShareTitle(): string {
    if (this.activeImageShareTab === 'pacs') {
      return 'IMPACT System';
    }
    if (this.activeImageShareTab === 'nuage') {
      return 'NuageDx Clear Canvas';
    }
    return '';
  }

  private refreshPatientIdBuckets(): void {
    const patientIds: SharingPatientId[] = Array.isArray(this.patient?.patientIDs) ? this.patient.patientIDs : [];

    if (!patientIds.length) {
      this.currentFacilityPatientId = null;
      this.otherPatientIDs = [];
      return;
    }

    this.currentFacilityPatientId = patientIds.find(id => id.facility === this.currentFacility) || null;
    this.otherPatientIDs = patientIds.filter(id => id.facility !== this.currentFacility);
  }

  private getMaxAllowedStep(): number {
    let max = 1;

    if (this.shareTypeSelected === 'image') {
      max = Math.max(max, 2);
    }

    if (this.shareDirection) {
      max = Math.max(max, 3);
    }

    if (this.selectedPatientId) {
      max = Math.max(max, 4);
    }

    return max;
  }

  private clampCurrentPageToAllowed(): void {
    const allowed = this.getMaxAllowedStep();
    if (this.currentPage > allowed) {
      this.currentPage = allowed;
    }
  }

  private resetState(): void {
    this.showFacilityRequests = false;
    this.currentPage = 1;
    this.totalPages = 4;
    this.selectedPatientId = null;
    this.currentFacilityPatientId = null;
    this.otherPatientIDs = [];
    this.shareDirection = null;
    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    this.shareTypeSelected = null;
    this.patientIdsResetToken = 0;
  }
}
